import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  PURGE_NOTE_MAX,
  PURGE_REASON_LABELS,
  STAFF_PURGE_REASONS,
  purgeConfirmation,
  purgeInputError,
  type PurgeReason,
} from '../../lib/portalAssets/purge';

// PURGE NOW — STAFF ONLY, IRREVERSIBLE (2026-10-09). The takedown control, kept
// visibly apart from Delete: a delete is a soft hide Kyck can undo, this removes
// the files. Inline for the same reason as DeleteAssetControl (no portaled
// dialog) and because the confirmation is the safety, so it must be on the page.
//
// ⚠️ Every sentence comes from the pure purgeConfirmation (purge.ts): it names
// the asset, says it cannot be undone, and does not claim a link already issued
// stops working at once.

export function PurgeAssetControl({
  asset,
  onPurge,
  pending,
  error,
}: {
  asset: { title?: string | null; originalFilename: string };
  onPurge: (input: { reason: PurgeReason; note: string | null }) => void;
  pending: boolean;
  error: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState<PurgeReason | ''>('');
  const [note, setNote] = useState('');
  const c = purgeConfirmation(asset);
  const problem = reason === '' ? 'Choose a reason for the purge.' : purgeInputError({ reason, note });

  if (!confirming) {
    return (
      <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
        Purge now…
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-md border border-destructive/50 p-3 text-sm" role="group" aria-label="Confirm purge">
      <p className="break-all font-medium text-foreground">{c.title}</p>
      {c.file && (
        <p className="break-all text-muted-foreground" data-testid="confirm-original-file">
          {c.file}
        </p>
      )}
      {c.body.map((line) => (
        <p key={line} className="text-muted-foreground">
          {line}
        </p>
      ))}
      <label className="block text-foreground">
        <span className="mb-1 block font-medium">Reason</span>
        <select
          className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm"
          value={reason}
          onChange={(e) => setReason(e.target.value as PurgeReason | '')}
        >
          <option value="">Choose…</option>
          {STAFF_PURGE_REASONS.map((r) => (
            <option key={r} value={r}>
              {PURGE_REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-foreground">
        <span className="mb-1 block font-medium">Note{reason === 'other' ? ' (required)' : ' (optional)'}</span>
        <textarea
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          rows={2}
          maxLength={PURGE_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="destructive"
          disabled={pending || problem !== null}
          onClick={() => reason !== '' && onPurge({ reason, note: note.trim() || null })}
        >
          {pending ? 'Purging…' : 'Purge permanently'}
        </Button>
        <Button size="sm" variant="ghost" disabled={pending} onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </div>
      {error && (
        <p className="text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
